import { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import WhatsAppLink from "../../components/WhatsAppLink";
import MultiCellulare from "../../components/MultiCellulare";
import MultiEmail from "../../components/MultiEmail";
import HistoryInput from "../../components/HistoryInput";
import { guardCreate } from "../../context/DuplicateGuard";
import SimilarContacts from "../../components/SimilarContacts";
import TitoloInput from "../../components/TitoloInput";
import { withCfSesso } from "../../lib/cfSesso";

const SIM_KEYS = ["cognome", "nome", "denominazione", "ragione_sociale"];
import { guardTarga } from "../../context/TargaGuard";
import { ANAG_TYPES, ANAG_GROUPS, displayName, SEDE_OPERATIVA_FIELDS } from "../../lib/anagConfig";
import { MoveAnagCategory, MoveAnagBulk } from "../../components/MoveAnagCategory";

const CAT_SING = { clienti: "Cliente", controparti: "Controparte", testimoni: "Testimone", segnalatori: "Segnalatore", mandanti: "Mandante", cessionari: "Cessionario", avvocati: "Avvocato", avvocati_ctp: "Avvocato CTP", medici_legali: "Medico legale", periti: "Perito", accertatori: "Accertatore", liquidatori: "Liquidatore", istruttori: "Istruttore", commercialisti: "Commercialista", operatori: "Operatore", compagnie: "Compagnia", agenzie: "Agenzia", carrozzieri: "Carrozziere", istituti_mediazione: "Istituto mediazione", mediaconciliazione: "Mediaconciliazione" };
import { formatDate } from "../../lib/format";
import PageHeader from "../../components/PageHeader";
import AutocompleteInput from "../../components/AutocompleteInput";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import CreatableTipoUfficio from "../../components/TipoUfficioField";
import CompagniaField from "../../components/CompagniaField";
import DateInput from "../../components/DateInput";
import VeicoliEditor from "../../components/VeicoliEditor";
import ControparteVeicolo from "../../components/ControparteVeicolo";
import CompagnieMultiSelect from "../../components/CompagnieMultiSelect";
import IspettoratiCascadeSelect from "../../components/IspettoratiCascadeSelect";
import CompagniaDettaglioDialog from "../../components/CompagniaDettaglioDialog";
import { CopiaDatiClienteButton } from "../../components/CopiaDatiClienteButton";
import { AreaRiservataPanel } from "../../components/AreaRiservataPanel";
import { GdprClientePanel } from "../../components/GdprClientePanel";
import CompagniaSezioni from "../../components/CompagniaSezioni";
import RichiediCompagniaDialog from "../../components/RichiediCompagniaDialog";
import CollaboratoriEditor from "../../components/CollaboratoriEditor";
import { Contact, Plus, Loader2, Search, Pencil, Trash2, Car, ArrowUp, CheckCircle2, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { useStoredWidth, ResizeHandle } from "../../components/ResizeHandle";
import { PortaleAccessoSection, PORTALE_ATYPES } from "../../components/PortaleAccessoSection";
import CampiPraticaEditor from "../../components/CampiPraticaEditor";

function Icon({ name, className }) {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
}

export default function Anagrafiche({ embed } = {}) {
  const [sideW, setSideW] = useStoredWidth("pgp_w_anagrafiche", 220, 160, 420);
  const [activeType, setActiveType] = useState(embed?.atype || "clienti");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [veicoli, setVeicoli] = useState([]);
  const [mainOpen, setMainOpen] = useState(true);
  const [detailRec, setDetailRec] = useState(null);
  const [view, setView] = useState("active");
  const [trashRows, setTrashRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(null);

  const cfg = useMemo(() => ANAG_TYPES.find((t) => t.key === activeType), [activeType]);
  const isComp = activeType === "compagnie";
  const compPool = useMemo(() => {
    if (!isComp) return {};
    const out = {};
    rows.forEach((r) => Object.entries(r || {}).forEach(([k, v]) => { if (typeof v === "string" && v.trim()) (out[k] = out[k] || []).push(v.trim()); }));
    return out;
  }, [rows, isComp]);
  const supportsCestino = !isComp;
  const [reqOpen, setReqOpen] = useState(false);
  const { user } = useAuth();
  const isTitolare = user && user.role === "titolare";
  const [bloccaCollegati, setBloccaCollegati] = useState(false);
  useEffect(() => { api.get("/studio/me").then(({ data }) => setBloccaCollegati(!!(data.settings || {}).blocca_cestino_collegati)).catch(() => {}); }, []);
  const toggleBlocca = async () => {
    const v = !bloccaCollegati; setBloccaCollegati(v);
    try { await api.patch("/studio/settings", { blocca_cestino_collegati: v }); toast.success(v ? "Blocco attivato" : "Blocco disattivato"); }
    catch (e) { setBloccaCollegati(!v); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const displayRows = useMemo(() => {
    if (activeType !== "compagnie") return rows;
    return [...rows].sort((a, b) => displayName(a).localeCompare(displayName(b), "it", { sensitivity: "base" }));
  }, [rows, activeType]);

  const load = async (type = activeType, q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/anagrafiche/${type}${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const loadTrash = async (type = activeType) => {
    try { const { data } = await api.get(`/anagrafiche/${type}/cestino/list`); setTrashRows(data); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (embed) return;
    setSearch(""); setView("active"); setSelected(new Set());
    load(activeType, "");
    loadTrash(activeType);
    /* eslint-disable-next-line */
  }, [activeType]);

  const runSearch = (e) => { e.preventDefault(); load(activeType, search); };

  // Ricerca dinamica: filtra mentre si digita (debounce), senza premere invio.
  useEffect(() => {
    if (embed) return;
    const t = setTimeout(() => { load(activeType, search); }, 200);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [search]);

  const openNew = () => { setEditing(null); setForm({}); setVeicoli([]); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    const { id, veicoli: v, ...rest } = r;
    setForm(rest);
    setVeicoli(v || []);
    setOpen(true);
  };

  // Modalità "embed": apre direttamente la scheda (es. dalla matita del segnalatore in pratica)
  useEffect(() => {
    if (!embed) return;
    if (embed.rec) { openEdit(embed.rec); return; }
    const parts = (embed.initialName || "").trim().split(" ");
    setEditing(null); setVeicoli([]);
    setForm({ titolo: "nessuno", cognome: parts[0] || "", nome: parts.slice(1).join(" ") });
    setOpen(true);
    /* eslint-disable-next-line */
  }, []);
  const onDialogOpenChange = (o) => { setOpen(o); if (!o && embed) embed.onClose?.(); };

  const simSlot = (f, fields) => {
    if (editing) return null;
    const last = [...fields].reverse().find((x) => SIM_KEYS.includes(x.key));
    if (!last || last.key !== f.key) return null;
    return (
      <div key={`sim-${f.key}`} className="sm:col-span-2">
        <SimilarContacts atype={activeType} nome={form.nome} cognome={form.cognome} denominazione={form.denominazione || form.ragione_sociale} onSelect={(r) => openEdit(r)} onCancel={() => setOpen(false)} testId="anag-similar" />
      </div>
    );
  };

  const setField = (key, val) => setForm((f) => withCfSesso(f, key, val));
  const [showAreaPwd, setShowAreaPwd] = useState(false);
  const [invioCred, setInvioCred] = useState(false);
  const generaPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%&*?";
    const buf = new Uint32Array(12);
    window.crypto.getRandomValues(buf);
    const pwd = Array.from(buf, (n) => chars[n % chars.length]).join("");
    setField("area_password", pwd);
    setShowAreaPwd(true);
    toast.success("Password generata: ricordati di salvare o inviare le credenziali");
  };
  const copiaMessaggio = async () => {
    const em = (form.area_email || form.email || "").trim();
    const pwd = (form.area_password || "").trim();
    if (!em || !pwd) { toast.error("Imposta prima email e password di accesso del segnalatore."); return; }
    const nome = [form.nome, form.cognome].filter(Boolean).join(" ") || form.denominazione || "";
    const msg = `Ciao${nome ? " " + nome : ""},\necco le credenziali per accedere alla tua area riservata segnalatore:\n\nLink: ${window.location.origin}/segnalatori\nEmail: ${em}\nPassword: ${pwd}\n\nAll'accesso riceverai un codice di verifica via email.`;
    try { await navigator.clipboard.writeText(msg); toast.success("Messaggio copiato: incollalo in WhatsApp"); }
    catch { toast.error("Copia non riuscita"); }
  };
  const copiaPassword = async () => {
    const pwd = form.area_password || "";
    if (!pwd) { toast.error("Nessuna password da copiare"); return; }
    try { await navigator.clipboard.writeText(pwd); toast.success("Password copiata negli appunti"); }
    catch { toast.error("Copia non riuscita: seleziona e copia manualmente"); }
  };
  const inviaCredenziali = async () => {
    if (!editing?.id) return;
    const em = (form.area_email || form.email || "").trim();
    if (!em || !(form.area_password || "").trim()) { toast.error("Imposta prima email e password di accesso del segnalatore."); return; }
    setInvioCred(true);
    try {
      await api.post(`/anagrafiche/segnalatori/${editing.id}/invia-credenziali`, {
        base_url: window.location.origin, area_email: form.area_email || "", area_password: form.area_password || "", email: form.email || "",
      });
      toast.success("Credenziali inviate via email al segnalatore");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setInvioCred(false); }
  };

  // Selezione comune di residenza -> auto-compila provincia + CAP
  const onComuneSelect = (field, item) => {
    if (field.fill && item?.provincia) {
      setForm((f) => ({ ...f, provincia: item.provincia, cap: item.cap || f.cap }));
    }
    if (field.fillNascita && item?.provincia) {
      setForm((f) => ({ ...f, provincia_nascita: item.provincia }));
    }
    if (field.fillOp && item?.provincia) {
      setForm((f) => ({ ...f, op_provincia: item.provincia, op_cap: item.cap || f.op_cap }));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    if (cfg.veicoli) payload.veicoli = veicoli;
    if (activeType === "controparti") payload.veicoli = veicoli;
    try {
      const plates = [form.targa, ...(((cfg.veicoli || activeType === "controparti") ? veicoli : []) || []).map((v) => v.targa)].filter(Boolean);
      for (const pl of plates) {
        const { action, match } = await guardTarga(pl, editing?.id);
        if (action === "existing" && match?.record) { openEdit(match.record); setSaving(false); return; }
      }
      let doc;
      if (editing) {
        ({ data: doc } = await api.patch(`/anagrafiche/${activeType}/${editing.id}`, payload));
        const pwdChanged = activeType === "segnalatori" && (form.area_password || "") !== (editing.area_password || "");
        toast.success(pwdChanged ? "Anagrafica aggiornata · password area riservata salvata" : "Anagrafica aggiornata");
      } else {
        const { action, record } = await guardCreate(activeType, payload);
        if (action === "existing") { openEdit(record); setSaving(false); return; }
        doc = record;
        toast.success("Anagrafica creata");
      }
      setOpen(false);
      if (embed) { embed.onSaved?.(doc); return; }
      load(activeType, search);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (supportsCestino) { moveToTrash([r.id]); return; }
    if (!window.confirm(`Eliminare "${displayName(r)}"?`)) return;
    try {
      await api.delete(`/anagrafiche/${activeType}/${r.id}`);
      toast.success("Eliminato");
      load(activeType, search);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const tabCls = (a) => `px-4 py-2 text-sm font-medium rounded-md transition-colors ${a ? "bg-amber-500 text-[#0a0d14]" : "text-slate-300 hover:bg-slate-800/60"}`;

  const moveToTrash = async (ids) => {
    const list = ids || [...selected]; if (!list.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/anagrafiche/${activeType}/check-links`, { ids: list });
      const linked = data.linked || [];
      if (linked.length) {
        if (bloccaCollegati) {
          toast.error(`Blocco attivo: ${linked.length === 1 ? "1 anagrafica è collegata" : linked.length + " anagrafiche sono collegate"} a pratiche attive e non può essere spostata nel cestino.`);
          setBusy(false); return;
        }
        const names = linked.map((l) => `• ${l.name} (${l.count} ${l.count === 1 ? "pratica attiva" : "pratiche attive"})`).join("\n");
        const ok = window.confirm(`Attenzione: ${linked.length === 1 ? "1 anagrafica è collegata" : linked.length + " anagrafiche sono collegate"} a pratiche attive:\n\n${names}\n\nSpostare comunque nel cestino?`);
        if (!ok) { setBusy(false); return; }
      }
      await api.post(`/anagrafiche/${activeType}/cestino/move`, { ids: list });
      toast.success(`${list.length} ${list.length === 1 ? "spostato" : "spostati"} nel cestino`);
      setSelected(new Set()); load(activeType, search); loadTrash();
    } catch (e) {
      if (e.response?.status === 409) toast.error("Blocco attivo: anagrafica collegata a pratiche attive.");
      else toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setBusy(false); }
  };
  const restore = async (ids) => {
    const list = ids || [...selected]; if (!list.length) return;
    setBusy(true);
    try { await api.post(`/anagrafiche/${activeType}/cestino/restore`, { ids: list }); toast.success(`${list.length} ${list.length === 1 ? "ripristinato" : "ripristinati"}`); setSelected(new Set()); load(activeType, search); loadTrash(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const permanentDelete = async (ids) => {
    const list = ids || [...selected]; if (!list.length) return;
    if (!window.confirm(`Eliminare definitivamente ${list.length} ${list.length === 1 ? "anagrafica" : "anagrafiche"}? L'operazione è irreversibile.`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/anagrafiche/${activeType}/cestino/delete`, { ids: list }); toast.success(`${data.deleted} ${data.deleted === 1 ? "eliminato" : "eliminati"} definitivamente`); setSelected(new Set()); loadTrash(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const bulkDelete = async () => {
    const all = [...selected]; if (!all.length) return;
    setBusy(true);
    let linked = [];
    try { const { data } = await api.post(`/anagrafiche/${activeType}/check-links`, { ids: all }); linked = data.linked || []; }
    catch { /* se il controllo fallisce, procediamo senza blocco */ }
    const linkedIds = new Set(linked.map((l) => l.id));
    let toMove = all;
    let blocked = [];
    if (linked.length) {
      if (bloccaCollegati) {
        blocked = linked;
        toMove = all.filter((id) => !linkedIds.has(id));
      } else {
        const names = linked.map((l) => `• ${l.name} (${l.count} ${l.count === 1 ? "pratica attiva" : "pratiche attive"})`).join("\n");
        const ok = window.confirm(`Attenzione: ${linked.length === 1 ? "1 anagrafica è collegata" : linked.length + " anagrafiche sono collegate"} a pratiche attive:\n\n${names}\n\nSpostare comunque nel cestino?`);
        if (!ok) { setBusy(false); return; }
      }
    }
    if (!toMove.length) {
      setProg({ open: true, total: all.length, done: all.length, moved: 0, blocked, status: "done" });
      setSelected(new Set()); setBusy(false); return;
    }
    const CHUNK = 20;
    let moved = 0;
    setProg({ open: true, total: toMove.length, done: 0, moved: 0, blocked, status: "running" });
    for (let i = 0; i < toMove.length; i += CHUNK) {
      const chunk = toMove.slice(i, i + CHUNK);
      try { const { data } = await api.post(`/anagrafiche/${activeType}/cestino/move`, { ids: chunk }); moved += data.moved || 0; }
      catch { /* eventuale 409 di race: la voce resta attiva */ }
      const done = Math.min(i + CHUNK, toMove.length);
      setProg((p) => ({ ...p, done, moved }));
    }
    setProg((p) => ({ ...p, done: toMove.length, moved, status: "done" }));
    setSelected(new Set()); load(activeType, search); loadTrash();
    setBusy(false);
  };

  const renderField = (f) => {
    if (f.key === "indirizzo") {
      return (
        <div className="mt-1.5">
          <AddressAutocomplete
            value={form.indirizzo}
            onChange={(v) => setField("indirizzo", v)}
            onPick={(p) => setForm((ff) => {
              const m = isComp ? String(p.indirizzo || "").trim().match(/^(.*?)[,\s]+(\d+[A-Za-z]?(?:\/\w+)?)$/) : null;
              return { ...ff, indirizzo: m ? m[1] : p.indirizzo, ...(m ? { numero_civico: m[2] } : {}), comune: p.comune, cap: p.cap, provincia: (p.provincia || "").toUpperCase() };
            })}
            city={form.comune}
            prov={form.provincia}
            testId="fld-indirizzo"
          />
        </div>
      );
    }
    if (f.type === "compagnie_multi") {
      return <CompagnieMultiSelect value={form[f.key] || []} onChange={(v) => setField(f.key, v)} testId={`fld-${f.key}`} />;
    }
    if (f.type === "ispettorati_multi") {
      return <IspettoratiCascadeSelect compagnie={form.compagnie || []} value={form[f.key] || []} onChange={(v) => setField(f.key, v)} testId={`fld-${f.key}`} />;
    }
    if (f.type === "tipoufficio") {
      return <CreatableTipoUfficio value={form[f.key]} onChange={(v) => setField(f.key, v)} testId={`fld-${f.key}`} />;
    }
    if (f.type === "titolo") {
      return <TitoloInput value={form[f.key]} onChange={(v) => setField(f.key, v)} testId={`fld-${f.key}`} className="mt-1.5 bg-slate-900/60 border-slate-700" />;
    }
    if (f.type === "select") {
      return (
        <Select value={form[f.key] || undefined} onValueChange={(v) => setField(f.key, v)}>
          <SelectTrigger data-testid={`fld-${f.key}`} className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
            {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (f.type === "checkbox") {
      return (
        <label className="mt-1.5 flex items-center gap-2 cursor-pointer" data-testid={`fld-${f.key}-wrap`}>
          <Checkbox checked={!!form[f.key]} onCheckedChange={(v) => setField(f.key, !!v)} data-testid={`fld-${f.key}`} />
          <span className="text-sm text-slate-300">{f.hint || "Sì"}</span>
        </label>
      );
    }
    if (f.type === "textarea") {
      return <Textarea value={form[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} rows={2} data-testid={`fld-${f.key}`} className="mt-1.5 bg-slate-900/60 border-slate-700" />;
    }
    if (f.type === "autocomplete") {
      if (f.source === "compagnie") {
        return (
          <div className="mt-1.5">
            <CompagniaField value={form[f.key]} onChange={(val) => setField(f.key, val)} testId={`fld-${f.key}`} />
          </div>
        );
      }
      return (
        <div className="mt-1.5">
          <AutocompleteInput
            value={form[f.key]}
            onChange={(val) => setField(f.key, val)}
            source={f.source}
            onSelectItem={(item) => onComuneSelect(f, item)}
            testId={`fld-${f.key}`}
          />
        </div>
      );
    }
    if (f.type === "date") {
      return <div className="mt-1.5"><DateInput value={form[f.key]} onChange={(v) => setField(f.key, v)} testId={`fld-${f.key}`} className="bg-slate-900/60 border-slate-700" /></div>;
    }
    if (f.key === "telefono" && activeType === "liquidatori") {
      return <MultiEmail value={form.telefono} extra={form.telefono_extra} label="telefono" type="tel" star={false} altLabel="altro telefono" testId="fld-telefono"
        onChange={(main, ex) => setForm((p) => ({ ...p, telefono: main, telefono_extra: ex }))} />;
    }
    if ((f.key === "email" || f.key === "pec") && cfg.group === "Professionisti") {
      const ek = `${f.key}_extra`;
      return <MultiEmail value={form[f.key]} extra={form[ek]} label={f.key === "pec" ? "PEC" : "email"} testId={`fld-${f.key}`}
        onChange={(main, ex) => setForm((p) => ({ ...p, [f.key]: main, [ek]: ex }))} />;
    }
    if (f.key === "cellulare") {
      return (
        <div className="mt-1.5">
          <MultiCellulare extra={form.cellulari_extra} onExtra={(v) => setField("cellulari_extra", v)} labels={form.cellulari_etichette} onLabels={(v) => setField("cellulari_etichette", v)} {...(activeType === "clienti" ? { principal: form.cell_principale, onPrincipal: (v) => setField("cell_principale", v) } : {})} testId="fld-cellulare">
            <Input type="tel" value={form.cellulare || ""} onChange={(e) => setField("cellulare", e.target.value)} data-testid="fld-cellulare" className="bg-slate-900/60 border-slate-700" />
          </MultiCellulare>
        </div>
      );
    }
    if (isComp && !f.type) {
      return <HistoryInput value={form[f.key]} onChange={(v) => setField(f.key, v)} options={compPool[f.key] || []} testId={`fld-${f.key}`}
        className={["codice_fiscale", "partita_iva"].includes(f.key) ? "font-mono" : ""} />;
    }
    return (
      <Input
        type={f.type === "email" ? "email" : f.type === "tel" ? "tel" : "text"}
        value={form[f.key] || ""}
        onChange={(e) => setField(f.key, e.target.value)}
        data-testid={`fld-${f.key}`}
        className={`mt-1.5 bg-slate-900/60 border-slate-700 ${["codice_fiscale", "partita_iva"].includes(f.key) ? "font-mono" : ""}`}
      />
    );
  };

  return (
    <div data-testid={embed ? "anag-embed" : "anagrafiche-page"}>
      {!embed && <>
      <PageHeader
        title="Anagrafiche"
        subtitle="Rubrica completa dello studio: clienti, controparti, professionisti, aziende ed enti."
        icon={Contact}
      />

      <div className="grid lg:grid-cols-[var(--side-w)_minmax(0,1fr)] gap-4 2xl:gap-6" style={{ "--side-w": `${sideW}px` }}>
        {/* Types sidebar */}
        <div className="relative h-fit">
        <ResizeHandle width={sideW} onChange={setSideW} testId="anag-side-resize-handle" className="hidden lg:block" />
        <div className="pgp-card p-3 h-fit pgp-fade-up max-h-[76vh] overflow-y-auto">
          {ANAG_GROUPS.map((g) => (
            <div key={g} className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono px-2 mb-1">{g}</div>
              {ANAG_TYPES.filter((t) => t.group === g && t.key !== "operatori").map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveType(t.key)}
                  data-testid={`anag-type-${t.key}`}
                  className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm mb-0.5 transition-colors ${
                    activeType === t.key ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" : "text-slate-300 hover:bg-slate-800/60 border border-transparent"
                  }`}
                >
                  <Icon name={t.icon} className="h-4 w-4 shrink-0" />
                  <span className="truncate">{t.label}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
        </div>

        {/* List */}
        <div className="pgp-fade-up min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Icon name={cfg.icon} className="h-5 w-5 text-amber-400" />
              <h2 className="font-head text-xl font-bold text-slate-100">{cfg.label}</h2>
              <Badge className="bg-slate-500/15 text-slate-400 border border-slate-500/30 font-mono">{rows.length}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <form onSubmit={runSearch} className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca…" data-testid="anag-search"
                  className="pl-9 pr-9 w-56 h-10 bg-slate-900/60 border-slate-700" />
                {search && (
                  <button type="button" onClick={() => setSearch("")} data-testid="anag-search-clear" aria-label="Azzera ricerca"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-100 rounded-full hover:bg-slate-700/60 p-0.5 transition-colors">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </form>
              {isComp ? (
                <Button onClick={() => setReqOpen(true)} data-testid="richiedi-compagnia-btn" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                  <Plus className="h-4 w-4 mr-1" /> Richiedi nuova compagnia
                </Button>
              ) : (
                <Button onClick={openNew} data-testid="anag-new-button" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                  <Plus className="h-4 w-4 mr-1" /> Nuovo
                </Button>
              )}
              {supportsCestino && view === "active" && selected.size > 0 && (
                <Button onClick={bulkDelete} disabled={busy} data-testid="anag-bulk-delete-top" className="bg-red-600 hover:bg-red-700 text-white font-semibold">
                  <Trash2 className="h-4 w-4 mr-1" /> Elimina selezionati ({selected.size})
                </Button>
              )}
            </div>
          </div>

          {supportsCestino && (
            <div className="flex items-center gap-2 mb-4" data-testid="anag-tabs">
              <button type="button" onClick={() => { setView("active"); setSelected(new Set()); }} data-testid="anag-tab-active" className={tabCls(view === "active")}>Attivi <span className="font-mono">({rows.length})</span></button>
              <button type="button" onClick={() => { setView("trash"); setSelected(new Set()); }} data-testid="anag-tab-trash" className={tabCls(view === "trash")}><Trash2 className="h-3.5 w-3.5 inline mr-1 -mt-0.5" /> Cestino <span className="font-mono">({trashRows.length})</span></button>
            </div>
          )}

          {isComp && (
            <p className="mb-4 text-xs text-slate-400" data-testid="compagnie-readonly-note">Elenco gestito centralmente dall'amministratore della piattaforma (sola lettura). Puoi gestire ispettorati e agenzie aprendo la compagnia.</p>
          )}
          {isTitolare && !isComp && (
            <label className="flex items-center gap-2 mb-4 text-sm text-slate-300 cursor-pointer" data-testid="anag-blocca-label">
              <Checkbox checked={bloccaCollegati} onCheckedChange={toggleBlocca} data-testid="anag-blocca-toggle" />
              Impedisci lo spostamento nel cestino di anagrafiche collegate a pratiche attive
            </label>
          )}

          <div className="pgp-card overflow-x-auto">
            {loading ? (
              <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
            ) : supportsCestino && view === "trash" ? (
              trashRows.length === 0 ? (
                <div className="p-10 text-center text-slate-400 text-sm" data-testid="anag-trash-empty">Il cestino è vuoto.</div>
              ) : (
                <table className="w-full text-sm" data-testid="anag-trash-table">
                  <thead>
                    <tr className="text-left text-slate-500 text-xs uppercase tracking-wider font-mono border-b border-slate-800">
                      <th className="px-3 2xl:px-5 py-3 w-10"><Checkbox checked={trashRows.length > 0 && trashRows.every((r) => selected.has(r.id))} onCheckedChange={(v) => setSelected(v ? new Set(trashRows.map((r) => r.id)) : new Set())} data-testid="anag-trash-select-all" /></th>
                      <th className="px-3 2xl:px-5 py-3">Denominazione</th>
                      <th className="px-3 2xl:px-5 py-3">Comune</th>
                      <th className="px-3 2xl:px-5 py-3 text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {trashRows.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-800/40 transition-colors" data-testid={`anag-trash-row-${r.id}`}>
                        <td className="px-3 2xl:px-5 py-3.5"><Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleSel(r.id)} data-testid={`anag-trash-check-${r.id}`} /></td>
                        <td className="px-3 2xl:px-5 py-3.5 font-medium text-slate-100">{displayName(r)}{r.codice_fiscale && <div className="text-[11px] text-slate-500 font-mono">{r.codice_fiscale}</div>}</td>
                        <td className="px-3 2xl:px-5 py-3.5 text-slate-300">{r.comune || "—"}{r.provincia ? ` (${r.provincia})` : ""}</td>
                        <td className="px-3 2xl:px-5 py-3.5">
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" onClick={() => restore([r.id])} disabled={busy} data-testid={`anag-trash-restore-${r.id}`} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"><ArrowUp className="h-4 w-4 mr-1" /> Ripristina</Button>
                            <Button size="sm" onClick={() => permanentDelete([r.id])} disabled={busy} data-testid={`anag-trash-delete-${r.id}`} className="h-8 bg-red-600 hover:bg-red-700 text-white font-semibold"><Trash2 className="h-4 w-4 mr-1" /> Elimina definitivamente</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : rows.length === 0 ? (
              <div className="p-10 text-center text-slate-400 text-sm">Nessun record. Aggiungi il primo.</div>
            ) : (
              <table className="w-full text-sm" data-testid="anag-table">
                <thead>
                  <tr className="text-left text-slate-500 text-xs uppercase tracking-wider font-mono border-b border-slate-800">
                    {supportsCestino && <th className="px-3 2xl:px-5 py-3 w-10"><Checkbox checked={displayRows.length > 0 && displayRows.every((r) => selected.has(r.id))} onCheckedChange={(v) => setSelected(v ? new Set(displayRows.map((r) => r.id)) : new Set())} data-testid="anag-select-all" /></th>}
                    {activeType === "compagnie" && <th className="px-3 2xl:px-5 py-3">Codice ANIA</th>}
                    <th className="px-3 2xl:px-5 py-3">Denominazione</th>
                    {activeType === "segnalatori" && <th className="px-3 2xl:px-5 py-3 hidden 2xl:table-cell">Abbrev. (copertina)</th>}
                    <th className="px-3 2xl:px-5 py-3">Comune</th>
                    <th className="px-3 2xl:px-5 py-3">Telefono</th>
                    <th className="px-3 2xl:px-5 py-3">Email</th>
                    {cfg.veicoli && <th className="px-3 2xl:px-5 py-3">Veicoli</th>}
                    <th className="px-3 2xl:px-5 py-3 text-right" aria-label="Azioni" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {displayRows.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-800/40 transition-colors" data-testid={`anag-row-${r.id}`}>
                      {supportsCestino && <td className="px-3 2xl:px-5 py-3.5"><Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleSel(r.id)} data-testid={`anag-check-${r.id}`} /></td>}
                      {activeType === "compagnie" && <td className="px-3 2xl:px-5 py-3.5 text-slate-300 font-mono" data-testid={`compagnia-ania-${r.id}`}>{r.codice_ivass || "—"}</td>}
                      <td className={`px-3 2xl:px-5 py-3.5 font-medium text-slate-100 ${activeType === "clienti" ? "uppercase" : ""}`}>
                        {activeType === "compagnie" ? (
                          <button type="button" onClick={() => setDetailRec(r)} data-testid={`compagnia-open-${r.id}`}
                            className="text-left text-cyan-300 hover:text-cyan-200 hover:underline font-medium">
                            {displayName(r)}
                          </button>
                        ) : (
                          displayName(r)
                        )}
                        <span className="ml-2 inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-px text-[10px] font-semibold normal-case tracking-wide text-amber-600 align-middle" data-testid={`anag-cat-badge-${r.id}`}>{CAT_SING[activeType] || cfg.label}</span>
                        {r.codice_fiscale && <div className="text-[11px] text-slate-500 font-mono">{r.codice_fiscale}</div>}
                      </td>
                      {activeType === "segnalatori" && (
                        <td className="px-3 2xl:px-5 py-3.5 text-slate-300 font-mono hidden 2xl:table-cell" data-testid={`segnalatore-abbrev-${r.id}`}>{r.abbreviazione || "—"}</td>
                      )}
                      <td className="px-3 2xl:px-5 py-3.5 text-slate-300">{r.comune || "—"}{r.provincia ? ` (${r.provincia})` : ""}</td>
                      <td className="px-3 2xl:px-5 py-3.5 text-slate-300 font-mono">
                        <span className="inline-flex items-center gap-1.5">
                          {Array.from(new Set([r.cellulare, r.cell, r.telefono, r.tel_casa, r.tel_ufficio].filter(Boolean))).join(" | ") || "—"}
                          <WhatsAppLink number={r.cellulare || r.cell} size="h-3.5 w-3.5" testId={`anag-cell-wa-${r.id}`} />
                        </span>
                      </td>
                      <td className="px-3 2xl:px-5 py-3.5 text-slate-300">
                        {r.email ? <div className="break-all">{r.email}</div> : null}
                        {r.pec ? <div className="text-[11px] text-emerald-300 font-mono break-all">PEC: {r.pec}</div> : null}
                        {!r.email && !r.pec ? "—" : null}
                      </td>
                      {cfg.veicoli && (
                        <td className="px-3 2xl:px-5 py-3.5">
                          <Badge className="bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 font-mono">
                            <Car className="h-3 w-3 mr-1" />{(r.veicoli || []).length}
                          </Badge>
                        </td>
                      )}
                      <td className="px-3 2xl:px-5 py-3.5 text-right whitespace-nowrap">
                        {!isComp && <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`anag-edit-${r.id}`} className="text-slate-400 hover:text-slate-100 hover:bg-slate-700/40"><Pencil className="h-4 w-4" /></Button>}
                        {!isComp && <MoveAnagCategory rec={r} atype={activeType} name={displayName(r)} onMoved={() => load(activeType, search)} />}
                        {!isComp && <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`anag-del-${r.id}`} className="text-red-400 hover:text-red-300 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {supportsCestino && view === "active" && selected.size > 0 && (
            <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0d1220] border-t border-slate-700 px-6 py-4 flex items-center justify-between shadow-2xl" data-testid="anag-action-bar">
              <span className="text-slate-200 font-medium" data-testid="anag-selection-counter">{selected.size} selezionati</span>
              <div className="flex gap-3">
              <MoveAnagBulk ids={[...selected]} atype={activeType} disabled={busy} onMoved={() => { setSelected(new Set()); load(activeType, search); }} />
              <Button onClick={() => bulkDelete()} disabled={busy} data-testid="anag-bulk-delete-btn" className="bg-red-600 hover:bg-red-700 text-white font-semibold"><Trash2 className="h-4 w-4 mr-1.5" /> Elimina selezionati</Button>
              </div>
            </div>
          )}

          {supportsCestino && view === "trash" && selected.size > 0 && (
            <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0d1220] border-t border-slate-700 px-6 py-4 flex items-center justify-between shadow-2xl" data-testid="anag-trash-action-bar">
              <span className="text-slate-200 font-medium" data-testid="anag-trash-selection-counter">{selected.size} selezionati</span>
              <div className="flex gap-3">
                <Button onClick={() => restore()} disabled={busy} data-testid="anag-trash-restore-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"><ArrowUp className="h-4 w-4 mr-1.5" /> Ripristina selezionati</Button>
                <Button onClick={() => permanentDelete()} disabled={busy} data-testid="anag-trash-delete-btn" className="bg-red-600 hover:bg-red-700 text-white font-semibold"><Trash2 className="h-4 w-4 mr-1.5" /> Elimina definitivamente</Button>
              </div>
            </div>
          )}
        </div>
      </div>
      </>}

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={onDialogOpenChange}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-3xl" style={{ display: "flex", flexDirection: "column", maxHeight: "88vh", overflow: "hidden", padding: 0 }}>
          <DialogHeader className="px-6 pt-6 shrink-0">
            <DialogTitle className="font-head">{editing ? "Modifica" : "Nuovo"} · {cfg.label}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
          <form id="anag-edit-form" onSubmit={save} className="space-y-4" data-testid="anag-form">
            {activeType === "clienti" && (
              <div className="flex justify-end" data-testid="anag-copia-cliente-wrap">
                <CopiaDatiClienteButton data={form} testId="anag-copia-dati-cliente" />
              </div>
            )}
            {activeType === "clienti" && editing?.id && (
              <AreaRiservataPanel anagId={editing.id} defaultEmail={form.email} />
            )}
            {activeType === "clienti" && editing?.id && (
              <GdprClientePanel anagId={editing.id} />
            )}
            {cfg.sections ? (
              cfg.sections.map((s) => (
                <div key={s.title} data-testid={`section-${s.title}`}>
                  <div style={{ backgroundColor: s.color }} className="px-4 py-2 rounded-t-lg text-white text-xs font-bold uppercase tracking-widest">{s.title}</div>
                  <div className="border border-t-0 border-slate-800 rounded-b-lg p-4 bg-slate-900/20">
                    {s.veicoli ? (
                      <VeicoliEditor veicoli={veicoli} onChange={setVeicoli} hideHeader />
                    ) : (
                      <div className="grid sm:grid-cols-2 gap-4">
                        {s.fields.map((f) => [
                          <div key={f.key} className={f.w === 2 ? "sm:col-span-2" : ""}>
                            <Label className="text-slate-300">{f.label}</Label>
                            {renderField(f)}
                          </div>,
                          simSlot(f, s.fields),
                        ])}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <>
                {(cfg.group === "Professionisti" || activeType === "carrozzieri") ? (
                  <div className="border border-slate-700 rounded-lg overflow-hidden">
                    <button type="button" onClick={() => setMainOpen((o) => !o)} data-testid="anag-main-toggle" className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-800/60 hover:bg-slate-800 text-left transition-colors">
                      <span className="text-xs font-bold uppercase tracking-widest text-slate-200">Dati {cfg.label}</span>
                      {mainOpen ? <Icons.ChevronDown className="h-4 w-4 text-slate-400" /> : <Icons.ChevronRight className="h-4 w-4 text-slate-400" />}
                    </button>
                    {mainOpen && (
                      <div className="p-4 grid sm:grid-cols-2 gap-4">
                        {cfg.fields.map((f) => [
                          <div key={f.key} className={f.w === 2 ? "sm:col-span-2" : ""}>
                            <Label className="text-slate-300">{f.label}</Label>
                            {renderField(f)}
                          </div>,
                          simSlot(f, cfg.fields),
                        ])}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {cfg.fields.map((f) => [
                      <div key={f.key} className={f.w === 2 ? "sm:col-span-2" : ""}>
                        <Label className="text-slate-300">{f.label}</Label>
                        {renderField(f)}
                      </div>,
                      simSlot(f, cfg.fields),
                    ])}
                  </div>
                )}

                {cfg.veicoli && (
                  <div className="border-t border-slate-800 pt-4">
                    <VeicoliEditor veicoli={veicoli} onChange={setVeicoli} />
                  </div>
                )}

                {(cfg.group === "Professionisti" || activeType === "carrozzieri") && (
                  <div className="border border-slate-700 rounded-lg overflow-hidden" data-testid="anag-sede-operativa">
                    <div className="px-4 py-2.5 bg-slate-800/60">
                      <span className="text-xs font-bold uppercase tracking-widest text-slate-200">Sede Operativa</span>
                      <span className="ml-2 text-[11px] text-slate-500">separata dalla sede legale / fiscale</span>
                    </div>
                    <div className="p-4 grid sm:grid-cols-2 gap-4">
                      {SEDE_OPERATIVA_FIELDS.map((f) => (
                        <div key={f.key} className={f.w === 2 ? "sm:col-span-2" : ""}>
                          <Label className="text-slate-300">{f.label}</Label>
                          {renderField(f)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {(cfg.group === "Professionisti" || activeType === "carrozzieri" || activeType === "segnalatori") && activeType !== "liquidatori" && (
              <CollaboratoriEditor value={form.collaboratori || []} onChange={(v) => setField("collaboratori", v)} testId="anag-collaboratori" />
            )}

            {activeType === "controparti" && (
              <ControparteVeicolo veicolo={veicoli[0] || null} onChange={(v) => setVeicoli(v ? [v] : [])} testId="anag-cp-veicolo" />
            )}

            {activeType === "segnalatori" && (
              <div data-testid="section-compenso">
                <div style={{ backgroundColor: "#9a3412" }} className="px-4 py-2 rounded-t-lg text-white text-xs font-bold uppercase tracking-widest">COMPENSO</div>
                <div className="border border-t-0 border-slate-800 rounded-b-lg p-4 bg-slate-900/20 space-y-4">
                  <div className="flex items-center gap-4">
                    <Label className="text-slate-300">Tipo Compenso</Label>
                    <div className="flex rounded-md overflow-hidden border border-slate-700">
                      {[["fisso", "Euro fisso"], ["percentuale", "Percentuale"]].map(([v, l]) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setField("compenso_tipo", v)}
                          data-testid={`fld-compenso-${v}`}
                          className={`px-4 py-1.5 text-sm font-semibold ${(form.compenso_tipo || "fisso") === v ? "bg-amber-500 text-[#0a0d14]" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {(form.compenso_tipo || "fisso") === "fisso" ? (
                      <div>
                        <Label className="text-slate-300">Importo (€)</Label>
                        <Input value={form.compenso_importo || ""} onChange={(e) => setField("compenso_importo", e.target.value)} data-testid="fld-compenso-importo" className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono" />
                      </div>
                    ) : (
                      <>
                        <div>
                          <Label className="text-slate-300">Percentuale (%)</Label>
                          <Input value={form.compenso_percentuale || ""} onChange={(e) => setField("compenso_percentuale", e.target.value)} data-testid="fld-compenso-perc" className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono" />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-slate-300">Base di calcolo</Label>
                          <Select value={form.compenso_base || undefined} onValueChange={(v) => setField("compenso_base", v)}>
                            <SelectTrigger data-testid="fld-compenso-base" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                            <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                              {["sugli onorari", "sul risarcimento", "sul danno", "altro"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeType === "segnalatori" && (
              <div data-testid="section-area-riservata">
                <div style={{ backgroundColor: "#0369a1" }} className="px-4 py-2 rounded-t-lg text-white text-xs font-bold uppercase tracking-widest">AREA RISERVATA SEGNALATORE</div>
                <div className="border border-t-0 border-slate-800 rounded-b-lg p-4 bg-slate-900/20 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Email accesso</Label>
                      <Input value={form.area_email || ""} autoComplete="off" name="seg-area-email" onChange={(e) => setField("area_email", e.target.value)} placeholder={form.email || "email@esempio.it"} data-testid="fld-area-email" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                    </div>
                    <div>
                      <Label className="text-slate-300">Password (visibile solo a te)</Label>
                      <div className="relative mt-1.5">
                        <Input type={showAreaPwd ? "text" : "password"} autoComplete="new-password" name="seg-area-password" value={form.area_password || ""} onChange={(e) => setField("area_password", e.target.value)} data-testid="fld-area-password" className="bg-slate-900/60 border-slate-700 font-mono pr-52" />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                          <button type="button" onClick={generaPassword} title="Genera password sicura" data-testid="genera-area-password" className="text-xs text-sky-400 hover:text-sky-300">Genera</button>
                          <span className="text-slate-600">|</span>
                          <button type="button" onClick={copiaPassword} title="Copia password negli appunti" data-testid="copia-area-password" className="text-xs text-teal-400 hover:text-teal-300">Copia</button>
                          <span className="text-slate-600">|</span>
                          <button type="button" onClick={() => setShowAreaPwd((s) => !s)} data-testid="toggle-area-password" className="text-xs text-amber-400 hover:text-amber-300">{showAreaPwd ? "Nascondi" : "Mostra"}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={!!form.area_archivio_visibile} onCheckedChange={(v) => setField("area_archivio_visibile", !!v)} data-testid="fld-area-archivio" />
                    <span className="text-sm text-slate-200">Mostra archivio compensi nell'area riservata</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={!!form.area_archivio_pratiche_visibile} onCheckedChange={(v) => setField("area_archivio_pratiche_visibile", !!v)} data-testid="fld-area-archivio-pratiche" />
                    <span className="text-sm text-slate-200">Mostra archivio pratiche nell'area riservata <span className="text-slate-500">(pratiche chiuse o archiviate)</span></span>
                  </label>
                  <div className="flex items-center gap-3 flex-wrap">
                    <button type="button" onClick={inviaCredenziali} disabled={!editing?.id || invioCred} data-testid="invia-credenziali-btn" className="h-9 inline-flex items-center px-4 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold disabled:opacity-50">
                      {invioCred ? "Invio…" : "Invia credenziali"}
                    </button>
                    <button type="button" onClick={copiaMessaggio} data-testid="copia-messaggio-credenziali" className="h-9 inline-flex items-center px-4 rounded-md border border-emerald-600 text-emerald-400 hover:bg-emerald-600/10 text-sm font-semibold">
                      Copia messaggio per WhatsApp
                    </button>
                    {!editing?.id && <span className="text-xs text-slate-500">Salva prima l'anagrafica per inviare le credenziali.</span>}
                  </div>
                </div>
              </div>
            )}

            {activeType === "segnalatori" && (
              <div className="border border-slate-700 rounded-lg p-4" data-testid="section-campi-pratica">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-200 mb-2">Campi visibili in pratica</div>
                <CampiPraticaEditor value={form.campi_pratica} onChange={(v) => setField("campi_pratica", v)} testId="anag-campi-pratica" />
              </div>
            )}

            {PORTALE_ATYPES.includes(activeType) && <PortaleAccessoSection atype={activeType} itemId={editing?.id} />}

            {(activeType === "carrozzieri" || activeType === "segnalatori") && (
              <div className="flex items-center gap-2" data-testid="section-cessionario-iniziali">
                <Checkbox checked={!!form.solo_iniziali_copertina} onCheckedChange={(v) => setField("solo_iniziali_copertina", !!v)} data-testid="fld-solo-iniziali-copertina" />
                <Label className="text-slate-300 cursor-pointer" onClick={() => setField("solo_iniziali_copertina", !form.solo_iniziali_copertina)}>Mostra solo le iniziali nella copertina</Label>
              </div>
            )}

          </form>
          {activeType === "compagnie" && editing && (
            <div className="border-t border-slate-800 pt-4" data-testid="compagnia-sezioni-edit">
              <CompagniaSezioni rec={editing} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} onSaved={(doc) => { setRows((rs) => rs.map((r) => (r.id === doc.id ? doc : r))); setEditing(doc); }} />
            </div>
          )}
          </div>
          <div className="shrink-0 border-t border-slate-700 px-6 py-3 flex justify-end" style={{ background: "#121824" }}>
            <Button type="submit" form="anag-edit-form" disabled={saving} data-testid="anag-submit" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Salva modifiche" : "Crea"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <RichiediCompagniaDialog open={reqOpen} onOpenChange={setReqOpen} />
      <CompagniaDettaglioDialog open={!!detailRec} onOpenChange={(o) => !o && setDetailRec(null)} rec={detailRec}
        onSaved={(doc) => { setRows((rs) => rs.map((r) => (r.id === doc.id ? doc : r))); setDetailRec(doc); }} />

      <Dialog open={!!prog?.open} onOpenChange={(o) => { if (!o && prog?.status === "done") setProg(null); }}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-md" data-testid="anag-progress-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2">
              {prog?.status === "done" ? <><CheckCircle2 className="h-5 w-5 text-emerald-400" /> Eliminazione completata</> : <><Loader2 className="h-5 w-5 animate-spin text-amber-400" /> Eliminazione in corso…</>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {(() => {
              const pct = prog && prog.total > 0 ? Math.round((prog.done / prog.total) * 100) : 0;
              return (
                <>
                  <div className="h-2.5 w-full bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${pct}%` }} data-testid="anag-progress-bar" />
                  </div>
                  <div className="text-sm text-slate-300 font-mono" data-testid="anag-progress-pct">{pct}% · {prog?.done || 0}/{prog?.total || 0}</div>
                </>
              );
            })()}
            {prog?.status === "done" && (
              <div className="text-sm text-slate-200 space-y-2 border-t border-slate-800 pt-3" data-testid="anag-progress-report">
                <p className="flex items-center gap-2 text-emerald-300"><CheckCircle2 className="h-4 w-4" /> {prog.moved} {prog.moved === 1 ? "anagrafica spostata" : "anagrafiche spostate"} nel cestino</p>
                {prog.blocked?.length > 0 && (
                  <div className="text-amber-300">
                    <p className="flex items-center gap-2"><Trash2 className="h-4 w-4" /> {prog.blocked.length} {prog.blocked.length === 1 ? "bloccata" : "bloccate"} perché collegate a pratiche attive:</p>
                    <ul className="mt-1 ml-6 list-disc text-xs text-slate-400 max-h-32 overflow-y-auto">
                      {prog.blocked.map((b) => <li key={b.id}>{b.name} · {b.count} {b.count === 1 ? "pratica" : "pratiche"}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {prog?.status === "done" && <Button onClick={() => setProg(null)} data-testid="anag-progress-close" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">Chiudi</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
